import React from "react";
import { intl } from "LocaleWrapper";
import { useStoreState } from "ezStore";
import { useApolloClient } from "@apollo/client";

import { Card, Table } from "@cofano/cck";
import usePropertyChangeEffect from "@cofano/cck/hooks/usePropertyChangeEffect";
import { Box, Tabs, Tab, Tooltip } from "@mui/material";
import { WarningAmberRounded } from "@mui/icons-material";

import useTabs from "components/hooks/useTabs";
import { TableId, useBackendTable } from "components/table";

import { DataFormat, Fields, hasOptionalFieldPermission, hasPluginPermission, usePermission } from "imports/Utils";

import { DossierLink, LocationLink, ProductLink, RelationLink, ShipLink, VoyageLink } from "utils/LinkUtils";
import { FormattedDate } from "utils/DateUtils";

import { Window } from "../LocationPlanning";
import { ChildPropsFromPagination } from "types/commons";
import {
	LocationPlanningPlannedActionsDocument,
	LocationPlanningPlannedActionsQuery,
	LocationPlanningUnplannedActionsDocument,
	LocationPlanningUnplannedActionsQuery,
} from "__generated__/view/locationplanning/graphql/locationplanning.queries.generated";
import { CallType, LocationType, SortDirection } from "__generated__/enums";

import ShipSelect from "view/ships/components/selects/ShipSelect";
import ProductSelect from "view/products/components/select/ProductSelect";
import LocationSelect from "view/locations/components/selects/LocationSelect";
import RelationSelect from "view/relations/components/selects/RelationSelect";
import DeliveryConditionSelect from "view/deliveryconditions/components/selects/DeliveryConditionSelect";
import { round } from "utils/numberUtils";

const TabItems = {
	planned: "planned",
	unplanned: "unplanned",
};

type CargosProps = {
	onStartDragging: (row: LocationPlanningUnplannedAction) => void;
	onFinishDragging: () => void;
	window: Window
	setScrollToDate: (date: number) => void;
	cargosRequestId: number;
};

type LocationPlanningPlannedAction = ChildPropsFromPagination<LocationPlanningPlannedActionsQuery, "cargoActions">;
export type LocationPlanningUnplannedAction = ChildPropsFromPagination<LocationPlanningUnplannedActionsQuery, "cargoActions">

const Cargos = ({
	onStartDragging,
	onFinishDragging,
	window,
	setScrollToDate,
	cargosRequestId,
}: CargosProps) => {

	const { selectedTab, selectTab } = useTabs({
		defaultSelected: TabItems.unplanned,
	});

	return (
		<React.Fragment>
			<Card disablePadding>
				<Box sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 0 }}>
					<Tabs
						value={selectedTab}
						onChange={(_event, value) => selectTab(value)}
						indicatorColor="primary">
						<Tab value={TabItems.planned} label={intl.formatMessage({ id: "PLANNED_CARGOS" })} />
						<Tab value={TabItems.unplanned} label={intl.formatMessage({ id: "UNPLANNED_CARGOS" })} />
					</Tabs>
				</Box>

				<div>
					{selectedTab === TabItems.planned && (
						<PlannedTable
							window={window}
							setScrollToDate={setScrollToDate}
							cargosRequestId={cargosRequestId}
						/>
					)}

					{selectedTab === TabItems.unplanned && (
						<UnplannedTable
							onStartDragging={onStartDragging}
							onFinishDragging={onFinishDragging}
							cargosRequestId={cargosRequestId}
						/>
					)}
				</div>
			</Card>
		</React.Fragment>
	);
};

const PlannedTable = ({
	window,
	setScrollToDate,
	cargosRequestId,
}: Pick<CargosProps, "window" | "setScrollToDate" | "cargosRequestId">) => {

	const session = useStoreState(store => store.session);
	const { isRTR, isCustomer, isOperator, isFinance } = usePermission();

	const client = useApolloClient();

	const { tableProps, refetch } = useBackendTable({
		id: TableId.LocationPlanningPlannedCargos,
		defaultSize: "COMPACT",
		defaultPageSize: 25,
		onChange: async (pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: LocationPlanningPlannedActionsDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "id",
								direction: SortDirection.Desc,
							} ],
						},
						search: {
							...filter,
							completeCargoWindow: window,
							ships: filter.ships?.map((it: { id: number }) => it.id),
							products: filter.products?.map((it: { id: number }) => it.id),
							loadingLocations: filter.loadingLocations?.map((it: { id: number }) => it.id),
							dischargingLocations: filter.dischargingLocations?.map((it: { id: number }) => it.id),
							loadingWindow: filter.loadingWindow ? { from: filter.loadingWindow[0], to: filter.loadingWindow[1] } : undefined,
							dischargingWindow: filter.dischargingWindow ? { from: filter.dischargingWindow[0], to: filter.dischargingWindow[1] } : undefined,
							locationTypes: [ LocationType.ExtractionMachine ],
							type: CallType.Loading,
							plannedOnQuay: true,
						},
					},
				});

				callback({
					content: result.data.cargoActions.content,
					totalResults: result.data.cargoActions.totalElements,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	usePropertyChangeEffect(() => {
		if (window.from && window.to) {
			refetch();
		}
	}, [ cargosRequestId, window ]);

	return (
		<React.Fragment>
			<Table
				{...tableProps}
				onRowClick={(row) => {
					const plannedAction = row as LocationPlanningPlannedAction;
					if (plannedAction.start) {
						setScrollToDate(plannedAction.start);
					}
				}}
				columns={[  {
					id: "location",
					title: intl.formatMessage({ id: "LOCATION" }),
					render: (row: LocationPlanningPlannedAction) => <LocationLink location={row.location} />,
					sortable: true,
					defaultVisible: false,
				}, {
					id: "start",
					title: intl.formatMessage({ id: "START" }),
					render: (row: LocationPlanningPlannedAction) => <FormattedDate date={row.start} withoutYear />,
					sortable: true,
					defaultVisible: false,
				}, {
					id: "end",
					title: intl.formatMessage({ id: "END" }),
					render: (row: LocationPlanningPlannedAction) => <FormattedDate date={row.end} withoutYear />,
					sortable: true,
					defaultVisible: false,
				}, {
					id: "loadingLocations",
					title: intl.formatMessage({ id: "ORDER_LOADING_PLACE" }),
					render: (row: LocationPlanningUnplannedAction) => (
						<LocationLink location={row.firstCorrespondingLoadingAction?.location} />
					),
					sortable: true,
					sortKey: "cargo.firstLoadingAction.location.description",
					filterComponent: (value, onChange) => (
						<LocationSelect
							label={intl.formatMessage({ id: "ORDER_LOADING_PLACE" })}
							value={value}
							onChange={onChange}
							types={[ LocationType.ExtractionMachine ]}
							multiple
						/>
					),
				}, {
					id: "loadingWindow",
					title: intl.formatMessage({ id: "ORDER_LOADING_DATE" }),
					render: (row: LocationPlanningUnplannedAction) => (
						<FormattedDate date={row.firstCorrespondingLoadingAction?.start} withoutYear />
					),
					sortable: true,
					sortKey: "cargo.firstLoadingAction.start",
					filterComponent: "DATE_RANGE",
					disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "loadingDate"),
				}, {
					id: "dischargingLocations",
					title: intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" }),
					render: (row: LocationPlanningUnplannedAction) => (
						<LocationLink location={row.lastCorrespondingDischargingAction?.location} />
					),
					sortable: true,
					sortKey: "cargo.lastDischargingAction.location.description",
					filterComponent: (value, onChange) => (
						<LocationSelect
							label={intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" })}
							value={value}
							onChange={onChange}
							types={[ LocationType.ExtractionMachine ]}
							multiple
						/>
					),
				}, {
					id: "dischargingWindow",
					title: intl.formatMessage({ id: "ORDER_DISCHARGING_DATE" }),
					render: (row: LocationPlanningUnplannedAction) => (
						<FormattedDate date={row.lastCorrespondingDischargingAction?.end} withoutYear />
					),
					sortable: true,
					sortKey: "cargo.lastDischargingAction.end",
					filterComponent: "DATE_RANGE",
					disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "dischargingDate"),
				}, {
					id: "ships",
					title: intl.formatMessage({ id: "SHIP" }),
					render: (row: LocationPlanningPlannedAction) => <ShipLink ship={row.call?.voyage.ship} />,
					sortable: true,
					sortKey: "call.voyage.ship.name",
					filterComponent: isRTR || isCustomer || isOperator || isFinance
						? (value, onChange) => (
							<ShipSelect
								label={intl.formatMessage({ id: "SHIP" })}
								value={value}
								onChange={(options) => onChange(options)}
								multiple />
						) : undefined,
				}, {
					id: "charterNumber",
					title: intl.formatMessage({ id: "CHARTER" }),
					render: (row: LocationPlanningPlannedAction) => <DossierLink charter={row.cargo.charter} />,
					sortable: true,
					sortKey: "cargo.charter.charterNumber",
					filterComponent: "TEXT",
				}, {
					id: "products",
					title: intl.formatMessage({ id: "PRODUCT" }),
					render: (row: LocationPlanningPlannedAction) => <ProductLink product={row.cargo.product} />,
					sortable: true,
					sortKey: "cargo.product.name",
					filterComponent: (value, onChange) => (
						<ProductSelect
							label={intl.formatMessage({ id: "PRODUCT" })}
							value={value}
							onChange={onChange}
							multiple
						/>
					),
				}, {
					id: "weight",
					title: intl.formatMessage({ id: "WEIGHT" }),
					render: (row: LocationPlanningPlannedAction) => DataFormat.tonnage(row.weight),
					sortable: true,
					sortKey: "metrics.weight",
				}, {
					id: "volume",
					title: intl.formatMessage({ id: "CARGO_VOLUME" }),
					render: (row: LocationPlanningPlannedAction) => DataFormat.volumeM3(row.volume),
					sortable: true,
					sortKey: "metrics.volume",
				}, {
					id: "voyage",
					title: intl.formatMessage({ id: "VOYAGE" }),
					render: (row: LocationPlanningPlannedAction) => <VoyageLink voyage={row.call?.voyage} />,
					sortable: true,
					sortKey: "call.voyage.voyageNumber",
				}, {
					id: "finished",
					title: intl.formatMessage({ id: "FINISHED" }),
					sortable: true,
					sortKey: "cargo.lastStatusChange.status.finishedStatus",
					render: (row: LocationPlanningPlannedAction) => DataFormat.boolean(row.cargo.lastStatusChange?.status.finishedStatus),
				} ]} />
		</React.Fragment>
	);
};

const UnplannedTable = ({
	onStartDragging,
	onFinishDragging,
	cargosRequestId,
}: Pick<CargosProps, "onStartDragging" | "onFinishDragging" | "cargosRequestId">) => {

	const session = useStoreState(store => store.session);
	const client = useApolloClient();
	const { isRTR, isCustomer, isOperator, isFinance, isReadOnly } = usePermission();

	const { tableProps, refetch } = useBackendTable({
		id: TableId.LocationPlanningUnplannedCargos,
		defaultSize: "COMPACT",
		defaultPageSize: 25,
		onChange: async (pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: LocationPlanningUnplannedActionsDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "id",
								direction: SortDirection.Desc,
							} ],
						},
						search: {
							...filter,
							ships: filter.ships?.map((it: { id: number }) => it.id),
							products: filter.products?.map((it: { id: number }) => it.id),
							loadingLocations: filter.loadingLocations?.map((it: { id: number }) => it.id),
							dischargingLocations: filter.dischargingLocations?.map((it: { id: number }) => it.id),
							loadingWindow: filter.loadingWindow ? { from: filter.loadingWindow[0], to: filter.loadingWindow[1] } : undefined,
							dischargingWindow: filter.dischargingWindow ? { from: filter.dischargingWindow[0], to: filter.dischargingWindow[1] } : undefined,
							debtors: filter.debtors?.map((it: { id: number }) => it.id),
							deliveryConditions: filter.deliveryConditions?.map((it: { id: number }) => it.id),
							locationTypes: [ LocationType.ExtractionMachine ],
							type: CallType.Loading,
							availableForQuayPlanning: true,
						},
					},
				});

				callback({
					content: result.data.cargoActions.content,
					totalResults: result.data.cargoActions.totalElements,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	usePropertyChangeEffect(() => {
		refetch(0);
	}, [ cargosRequestId ]);

	const calculateDuration = (seconds: number | null): string =>
		(seconds !== null ? DataFormat.minutesToTime(round(seconds / 60, 0)) : null) ?? intl.formatMessage({ id: "UNKNOWN" });

	return (
		<React.Fragment>
			<Table
				{...tableProps}
				dragAndDropEnabled={!isReadOnly}
				dragDropSettings={{
					onDragStart: (_event, row) => {
						onStartDragging(row);
					},
					onDragEnd: () => {
						onFinishDragging();
					},
					renderPreview: (row: LocationPlanningUnplannedAction) => {
						return (
							<div className="cargo-drag">
								{`${row.firstCorrespondingLoadingAction?.location?.description} - ${row.cargo.product?.name}`}
							</div>
						);
					},
				}}
				columns={[ {
					id: "charterNumber",
					title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
					render: (row: LocationPlanningUnplannedAction) => <DossierLink charter={row.cargo.charter} />,
					sortable: true,
					sortKey: "cargo.charter.charterNumber",
					disabled: !isRTR,
					width: 150,
					filterComponent: "TEXT",
				}, {
					id: "ships",
					title: intl.formatMessage({ id: "SHIP" }),
					render: (row: LocationPlanningUnplannedAction) => <ShipLink ship={row.call?.voyage.ship} />,
					filterComponent: isRTR || isCustomer || isOperator || isFinance
						? (value, onChange) => (
							<ShipSelect
								label={intl.formatMessage({ id: "SHIP" })}
								value={value}
								onChange={(options) => onChange(options)}
								multiple />
						) : undefined,
					sortable: true,
					sortKey: "call.voyage.ship.name",
					disabled: !hasPluginPermission(session, "voyages"),
					width: 200,
				}, {
					id: "deliveryConditions",
					title: intl.formatMessage({ id: "DELIVERY_CONDITION" }),
					sortable: true,
					disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "deliveryCondition"),
					defaultPinned: true,
					sortKey: "cargo.deliveryCondition",
					render: (row: LocationPlanningUnplannedAction) => row.cargo.deliveryCondition?.description ?? "",
					filterComponent: (value, onChange) => (
						<DeliveryConditionSelect
							label={intl.formatMessage({ id: "DELIVERY_CONDITION" })}
							value={value}
							onChange={onChange}
							disableSearch
							multiple
						/>
					),
				}, {
					id: "debtors",
					title: intl.formatMessage({ id: "ORDER_CUSTOMER" }),
					render: (row: LocationPlanningUnplannedAction) => <RelationLink relation={row.cargo.charter?.debtor} />,
					sortable: true,
					sortKey: "cargo.charter.debtor.name",
					disabled: !(hasOptionalFieldPermission(session, Fields.ORDER, "relation")),
					filterComponent: isRTR ? (value, onChange) => (
						<RelationSelect
							label={intl.formatMessage({ id: "ORDER_CUSTOMER" })}
							value={value}
							onChange={onChange}
							multiple
						/>
					) : undefined,
				}, {
					id: "products",
					title: intl.formatMessage({ id: "PRODUCT" }),
					render: (row: LocationPlanningUnplannedAction) => <ProductLink product={row.cargo.product} />,
					sortable: true,
					sortKey: "cargo.product.name",
					disabled: !hasPluginPermission(session, "products"),
					filterComponent: (value, onChange) => (
						<ProductSelect
							label={intl.formatMessage({ id: "PRODUCT" })}
							value={value}
							onChange={onChange}
							multiple
						/>
					),
				}, {
					id: "weight",
					title: intl.formatMessage({ id: "WEIGHT" }),
					render: (row: LocationPlanningUnplannedAction) => DataFormat.tonnage(row.weight),
					disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "weight"),
				}, {
					id: "volume",
					title: intl.formatMessage({ id: "CARGO_VOLUME" }),
					render: (row: LocationPlanningUnplannedAction) => DataFormat.volumeTon(row.volume),
					disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "volume"),
					defaultVisible: false,
				}, {
					id: "loadingLocations",
					title: intl.formatMessage({ id: "ORDER_LOADING_PLACE" }),
					render: (row: LocationPlanningUnplannedAction) => (
						<LocationLink location={row.firstCorrespondingLoadingAction?.location} />
					),
					sortable: true,
					sortKey: "cargo.firstLoadingAction.location.description",
					filterComponent: (value, onChange) => (
						<LocationSelect
							label={intl.formatMessage({ id: "ORDER_LOADING_PLACE" })}
							value={value}
							onChange={onChange}
							types={[ LocationType.ExtractionMachine ]}
							multiple
						/>
					),
				}, {
					id: "loadingWindow",
					title: intl.formatMessage({ id: "ORDER_LOADING_DATE" }),
					render: (row: LocationPlanningUnplannedAction) => (
						<FormattedDate date={row.firstCorrespondingLoadingAction?.start} withoutYear />
					),
					sortable: true,
					sortKey: "cargo.firstLoadingAction.start",
					filterComponent: "DATE_RANGE",
					disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "loadingDate"),
				}, {
					id: "dischargingLocations",
					title: intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" }),
					render: (row: LocationPlanningUnplannedAction) => (
						<LocationLink location={row.lastCorrespondingDischargingAction?.location} />
					),
					sortable: true,
					sortKey: "cargo.lastDischargingAction.location.description",
					filterComponent: (value, onChange) => (
						<LocationSelect
							label={intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" })}
							value={value}
							onChange={onChange}
							types={[ LocationType.ExtractionMachine ]}
							multiple
						/>
					),
				}, {
					id: "dischargingWindow",
					title: intl.formatMessage({ id: "ORDER_DISCHARGING_DATE" }),
					render: (row: LocationPlanningUnplannedAction) => (
						<FormattedDate date={row.lastCorrespondingDischargingAction?.end} withoutYear />
					),
					sortable: true,
					sortKey: "cargo.lastDischargingAction.end",
					filterComponent: "DATE_RANGE",
					disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "dischargingDate"),
				}, {
					id: "processingTime",
					title: intl.formatMessage({ id: "HANDLING_DURATION" }),
					width: 125,
					render: (row: LocationPlanningUnplannedAction) =>
						<span style={ { display: "flex", alignItems: "center" } }>
							{calculateDuration(row.processingTime?.durationInSeconds ?? null) }

							{row.processingTime?.warnings && row.processingTime.warnings.length > 0 &&
								<Tooltip
									title={(
										<span style={{ display: "flex", flexDirection: "column", textAlign: "center" }}>
											{row.processingTime.warnings.map(warning => (
												<span key={warning}>{intl.formatMessage({ id: warning })}</span>
											))}
										</span>
									)}>
									<span>
										<WarningAmberRounded fontSize="small" color="error" style={ { transform: "scale(0.8)" } } />
									</span>
								</Tooltip>
							}
						</span>,
					sortable: false,
					defaultVisible: true,
				} ] }
			/>
		</React.Fragment>
	);
};

export default Cargos;
